import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, FileText, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { api, BACKEND, errMsg } from "@/lib/api";
import { uploadFile } from "@/components/admin/ImageUpload";

const FileCard = ({ f, onDelete }) => {
  const [copied, setCopied] = useState(false);
  const link = BACKEND + f.url;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      window.prompt("Copy this link", link);
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden flex flex-col" data-testid={`media-file-${f.id}`}>
      <a href={link} target="_blank" rel="noreferrer" className="h-40 bg-stone-50 flex items-center justify-center">
        {f.content_type.startsWith("image/") ? <img src={link} alt="" loading="lazy" className="max-h-full max-w-full object-contain" /> : <FileText className="w-10 h-10 text-crimson" />}
      </a>
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="text-xs text-stone-600 truncate" title={f.original_filename}>{f.original_filename}</div>
        <input readOnly value={link} onFocus={(e) => e.target.select()} className="text-[11px] border border-stone-200 rounded px-2 py-1 bg-stone-50 w-full" data-testid={`media-link-${f.id}`} />
        <div className="flex gap-2 mt-auto">
          <button onClick={copy} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold bg-charcoal text-white rounded px-2 py-1.5" data-testid={`media-copy-${f.id}`}>
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{copied ? "Copied" : "Copy link"}
          </button>
          <button onClick={() => onDelete(f)} className="p-1.5 text-red-600 border border-red-200 rounded hover:bg-red-50" data-testid={`media-delete-${f.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(0);
  const input = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/files");
    setFiles(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pick = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(list.length);
    let ok = 0;
    for (const f of list) {
      try {
        await uploadFile(f);
        ok += 1;
      } catch (err) {
        toast.error(`${f.name}: ${errMsg(err)}`);
      }
      setBusy((b) => b - 1);
    }
    if (ok) toast.success(`${ok} file(s) uploaded`);
    load();
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"? Any page using this link will stop showing it.`)) return;
    try {
      await api.delete(`/admin/files/${f.id}`);
      toast.success("File deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Media Library</h1>
          <p className="text-sm text-stone-500 mt-1">Upload images or PDFs and copy their permanent link to use anywhere. {files.length} file(s).</p>
        </div>
        <button onClick={() => input.current?.click()} disabled={busy > 0} className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white rounded-md px-5 py-2.5 text-sm font-bold disabled:opacity-60" data-testid="media-upload-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}{busy ? `Uploading ${busy}…` : "Upload files"}
        </button>
        <input ref={input} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid="media-upload-input" />
      </div>
      {files.length ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {files.map((f) => <FileCard key={f.id} f={f} onDelete={del} />)}
        </div>
      ) : (
        <p className="text-sm text-stone-400 border border-dashed border-stone-300 rounded-md p-10 text-center" data-testid="media-empty">No files uploaded yet.</p>
      )}
    </div>
  );
}
